import React, { useContext, useEffect, useState } from 'react'
import { Form, Row, Col, Badge } from 'react-bootstrap'
import RecipeBuilderContext from './RecipeBuilderContext'

// Maps the TableColumn `display_as` enum to a short, human-readable label
// surfaced as a badge next to each column option.
const DATA_TYPE_LABELS = {
  text: 'text',
  date: 'date',
  money: 'money',
  two_digit_money: 'money',
  percent: 'percent',
  boolean: 'bool',
  account_segment: 'segment',
  link: 'link',
  status: 'status',
  flag_count: 'count',
  risk_score: 'score',
  time_in_queue: 'duration'
}

// Predicate-side type hint → set of TableColumn.display_as values that
// satisfy the hint. The recipe-builder filters the column dropdown by
// these so an admin can't pick a text column for an `amount_gt` param.
// `any` (or absence) disables the filter entirely.
const COLUMN_TYPE_COMPATIBILITY = {
  numeric: new Set(['money', 'two_digit_money', 'percent', 'risk_score', 'flag_count', 'time_in_queue']),
  date: new Set(['date']),
  text: new Set(['text', 'account_segment', 'link', 'status'])
}

const filterColumnsForParam = (columns, hint) => {
  if (!hint || hint === 'any') return columns
  const allowed = COLUMN_TYPE_COMPATIBILITY[hint]
  if (!allowed) return columns
  return columns.filter((col) => allowed.has(col.display_as))
}

// Renders one labeled input per kwarg in the predicate schema. The parent
// owns the node state and writes back via onChange(newNode).
const LeafPredicateForm = ({ node, schema, onChange }) => {
  const { auditColumns } = useContext(RecipeBuilderContext)
  if (!schema) {
    return <Badge bg="light" text="primary">Loading schema…</Badge>
  }

  // Params the section fills itself (audit-log columns) — hidden here so
  // nobody picks a grid column that isn't in the analyzed dataframe.
  const supplied = (param) => param.source === 'section_audit_columns' && !!auditColumns?.[param.name]
  const visibleParams = (schema.params || []).filter((param) => !supplied(param))
  const suppliedNames = (schema.params || []).filter(supplied).map((p) => auditColumns[p.name])

  const setParam = (paramName, value) => {
    const next = { ...node, [paramName]: value }
    if (value === '' || value === undefined || value === null) {
      delete next[paramName]
    }
    onChange(next)
  }

  return (
    <div>
      {visibleParams.map((param) => (
        <Row key={param.name} className="mb-2 align-items-center">
          <Col xs={4} className="text-end small text-muted pe-2">
            {param.ui_label || param.name}
            {param.required ? <span className="text-danger ms-1">*</span> : null}
          </Col>
          <Col xs={8}>
            <ParamInput param={param} value={node[param.name]} onChange={(v) => setParam(param.name, v)} />
          </Col>
        </Row>
      ))}
      {suppliedNames.length > 0 && (
        <Form.Text className="text-muted">
          Reads this section's change log ({suppliedNames.join(', ')}).
        </Form.Text>
      )}
    </div>
  )
}

const ParamInput = ({ param, value, onChange }) => {
  const placeholder = param.default !== undefined ? `default: ${formatDefault(param.default)}` : ''
  switch (param.type) {
    case 'number':
    case 'integer':
      return (
        <Form.Control
          size="sm"
          type="number"
          step={param.type === 'integer' ? 1 : 'any'}
          min={param.min}
          max={param.max}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={placeholder}
        />
      )
    case 'boolean':
      return (
        <Form.Check
          type="switch"
          checked={value === undefined ? !!param.default : !!value}
          onChange={(e) => onChange(e.target.checked)}
        />
      )
    case 'enum':
      return (
        <Form.Select size="sm" value={value ?? param.default ?? ''} onChange={(e) => onChange(e.target.value)}>
          {!param.required && <option value="">— default —</option>}
          {(param.enum || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Form.Select>
      )
    case 'enum_list':
      return <EnumListInput param={param} value={value} onChange={onChange} />
    case 'date':
      return (
        <Form.Control
          size="sm"
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'date_list':
      return <CommaListInput placeholder="2026-01-01, 2026-07-04" value={value} onChange={onChange} />
    case 'string_list':
      return <CommaListInput placeholder="value1, value2" value={value} onChange={onChange} />
    case 'column_list':
      return <ColumnListInput value={value} onChange={onChange} columnDataType={param.column_data_type} />
    case 'column_or_list':
      return <ColumnListInput value={value} onChange={onChange} columnDataType={param.column_data_type} allowSingle />
    case 'column':
      return <ColumnInput value={value} onChange={onChange} placeholder={placeholder} columnDataType={param.column_data_type} />
    case 'table_name':
    case 'string':
    default:
      return (
        <Form.Control
          size="sm"
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )
  }
}

// Single-column dropdown driven by the (muni, section) column list from
// RecipeBuilderContext. Falls back to a free-text input when no columns
// have been loaded yet (e.g. waiting for the section_columns request) so
// the user can still type a database_column_name manually.
const ColumnInput = ({ value, onChange, placeholder, columnDataType }) => {
  const { columns, isLoading } = useContext(RecipeBuilderContext)
  const filtered = filterColumnsForParam(columns, columnDataType)

  if (isLoading) {
    return <Form.Control size="sm" type="text" disabled placeholder="Loading columns…" />
  }
  if (!filtered || filtered.length === 0) {
    return (
      <div>
        <Form.Control
          size="sm"
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'database_column_name'}
        />
        {columnDataType && columnDataType !== 'any' && columns?.length > 0 && (
          <Form.Text className="text-muted">
            No {columnDataType} columns in this module — typing the name lets you continue.
          </Form.Text>
        )}
      </div>
    )
  }
  return (
    <Form.Select size="sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">— pick a {columnDataType && columnDataType !== 'any' ? `${columnDataType} ` : ''}column —</option>
      {filtered.map((col) => (
        <option key={col.id} value={col.database_column_name}>
          {col.system_display_name || col.name} ({col.database_column_name}) · {DATA_TYPE_LABELS[col.display_as] || col.display_as}
        </option>
      ))}
    </Form.Select>
  )
}

// Multi-column input — checkboxes for each available column. Falls back
// to a comma-separated text input when columns aren't available.
//
// `allowSingle` is the `column_or_list` semantic: emit a string when
// exactly one column is picked, an array otherwise (matches what the
// predicates accept).
const ColumnListInput = ({ value, onChange, allowSingle = false, columnDataType }) => {
  const { columns, isLoading } = useContext(RecipeBuilderContext)
  const filtered = filterColumnsForParam(columns, columnDataType)
  const selected = new Set(Array.isArray(value) ? value : value ? [value] : [])

  if (isLoading) {
    return <Form.Control size="sm" disabled placeholder="Loading columns…" />
  }
  if (!filtered || filtered.length === 0) {
    return (
      <div>
        <CommaListInput value={value} onChange={onChange} placeholder="col1, col2" expandSingle={allowSingle} />
        {columnDataType && columnDataType !== 'any' && columns?.length > 0 && (
          <Form.Text className="text-muted">
            No {columnDataType} columns in this module — type names manually.
          </Form.Text>
        )}
      </div>
    )
  }
  const toggle = (dbName) => {
    const next = new Set(selected)
    if (next.has(dbName)) next.delete(dbName)
    else next.add(dbName)
    const arr = Array.from(next)
    if (arr.length === 0) return onChange('')
    if (allowSingle && arr.length === 1) return onChange(arr[0])
    onChange(arr)
  }
  return (
    <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
      {filtered.map((col) => (
        <Form.Check
          key={col.id}
          type="checkbox"
          id={`col-${col.id}`}
          checked={selected.has(col.database_column_name)}
          onChange={() => toggle(col.database_column_name)}
          label={
            <span>
              {col.system_display_name || col.name} <code className="small text-muted">({col.database_column_name})</code>
              <Badge bg="light" text="primary" className="ms-1">{DATA_TYPE_LABELS[col.display_as] || col.display_as}</Badge>
            </span>
          }
        />
      ))}
    </div>
  )
}

// Multi-value text input that parses on `,`. We keep the raw text in
// local state so the user can type commas, trailing spaces, and partial
// tokens without the controlled re-render stripping them mid-keystroke.
// The parsed array gets pushed to the parent on every change for live
// validation; the raw text re-syncs to the parent only when the parent
// changes the value externally (e.g. switching to a different leaf).
const CommaListInput = ({ value, onChange, placeholder, expandSingle = false }) => {
  const arrFromValue = Array.isArray(value) ? value : value ? [value] : []
  const [text, setText] = useState(arrFromValue.join(', '))

  // Re-sync from the parent only when the underlying array actually
  // differs from what we'd parse out of our current text — otherwise
  // every keystroke loops through the parent and back, clobbering the
  // raw text the user is mid-typing.
  useEffect(() => {
    const parsedFromText = text
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    const same = arrFromValue.length === parsedFromText.length
      && arrFromValue.every((v, i) => v === parsedFromText[i])
    if (!same) {
      setText(arrFromValue.join(', '))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(arrFromValue)])

  const handleChange = (e) => {
    const next = e.target.value
    setText(next)
    const items = next
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    if (items.length === 0) return onChange('')
    if (expandSingle && items.length === 1) return onChange(items[0])
    onChange(items)
  }

  return (
    <Form.Control
      size="sm"
      type="text"
      value={text}
      placeholder={placeholder}
      onChange={handleChange}
    />
  )
}

const EnumListInput = ({ param, value, onChange }) => {
  const selected = new Set(Array.isArray(value) ? value : value ? [value] : (param.default || []))
  return (
    <div>
      {(param.enum || []).map((opt) => (
        <Form.Check
          key={opt}
          inline
          type="checkbox"
          id={`enum-${param.name}-${opt}`}
          label={opt}
          checked={selected.has(opt)}
          onChange={(e) => {
            const next = new Set(selected)
            if (e.target.checked) next.add(opt)
            else next.delete(opt)
            onChange(Array.from(next))
          }}
        />
      ))}
    </div>
  )
}

const formatDefault = (def) => {
  if (Array.isArray(def)) return `[${def.join(', ')}]`
  if (typeof def === 'boolean') return def ? 'on' : 'off'
  return String(def)
}

export default LeafPredicateForm
